import { HugeiconsIcon } from '@hugeicons/react'
import * as RadixSelect from '@radix-ui/react-select'
import Fuse from 'fuse.js/min-basic'
import clsx from 'clsx'
import { ArrowDown01Icon, Tick01Icon } from '@hugeicons/core-stroke-rounded'
import { Cancel01Icon, Search01Icon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  Combobox,
  ComboboxItem,
  ComboboxList,
  ComboboxProvider
} from '@ariakit/react'

import Loading from './Loading'

interface Option {
  value: string
  label: string
}

export type InputProps = {
  disabled?: boolean
  readOnly?: boolean
  ariaLabel?: string
  value?: string
  name?: string
  placeholder?: string
  invalid?: boolean
  onChange: (newValue: string) => void
}

type Props = InputProps & {
  options: Option[]
  searchText?: string

  search?: string
  loading?: boolean
  onChangeSearch?: (newSearch: string) => void

  allowSearchValue?: boolean
  searchThreshold?: number
}

export default function ComboBox({
  options,
  invalid,
  name,
  value,
  ariaLabel,
  placeholder,
  searchText,
  onChange,
  readOnly = false,
  disabled = false,

  loading = false,
  allowSearchValue = true,
  searchThreshold = 0.6,
  search,
  onChangeSearch
}: Props) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [internalSearchValue, setInternalSearchValue] = useState('')

  const searchValue = search ?? internalSearchValue

  const optionsFuse = useMemo(() => {
    return new Fuse(options, {
      keys: allowSearchValue ? ['label', 'value'] : ['label'],
      threshold: searchThreshold
    })
  }, [options, searchThreshold, allowSearchValue])

  const matches = useMemo(() => {
    if (!searchValue) return options

    const matches = optionsFuse.search(searchValue).map(result => result.item)

    // Radix Select does not work if we don't render the selected item, so we
    // make sure to include it in the list of matches.
    const selected = options.find(opt => opt.value === value)
    if (selected && !matches.includes(selected)) matches.push(selected)

    return matches
  }, [searchValue, optionsFuse, value, options])

  const onUpdateSearch = useCallback(
    (value: string) => {
      if (onChangeSearch) onChangeSearch(value)
      else setInternalSearchValue(value)
    },
    [setInternalSearchValue, onChangeSearch]
  )

  useEffect(() => {
    if (!open) onUpdateSearch('')
  }, [open, onUpdateSearch])

  return (
    <RadixSelect.Root
      value={value}
      onValueChange={onChange}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled || readOnly}
    >
      <ComboboxProvider
        open={open}
        setOpen={setOpen}
        resetValueOnHide
        includesBaseElement={false}
        setValue={value => startTransition(() => onUpdateSearch(value))}
      >
        <div className="relative">
          <RadixSelect.Trigger
            aria-label={ariaLabel}
            name={name}
            disabled={disabled || readOnly}
            className={clsx(
              'w-full flex flex-row items-center gap-1 rounded-small bg-white text-left',
              !!value && !readOnly && 'pr-8',
              !readOnly && 'border border-text select-none py-1.5 px-2 pr-4',
              readOnly && 'p-0 cursor-text',
              invalid && 'border-red-500'
            )}
          >
            {!readOnly && (
              <RadixSelect.Icon>
                <HugeiconsIcon
                  icon={ArrowDown01Icon}
                  className="size-4 text-text"
                />
              </RadixSelect.Icon>
            )}
            <RadixSelect.Value placeholder={placeholder} />
          </RadixSelect.Trigger>
          {!!value && !readOnly && (
            <button
              tabIndex={0}
              onClick={() => onChange('')}
              className="flex flex-row justify-center items-center p-1 size-5 absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
              aria-label={t`Reset`}
              type="button"
            >
              <HugeiconsIcon
                icon={Cancel01Icon}
                className="size-full text-text"
              />
            </button>
          )}
        </div>
        <RadixSelect.Content
          role="dialog"
          aria-label={ariaLabel}
          position="popper"
          className="max-h-(--radix-select-content-available-height) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-1 rounded-small bg-white shadow-md outline-hidden overflow-hidden z-popover"
        >
          <div className="flex flex-row items-center gap-2.5 p-3">
            <HugeiconsIcon icon={Search01Icon} />
            <Combobox
              autoSelect
              placeholder={searchText}
              className="w-full rounded-small border border-text bg-white text-regular text-text placeholder:text-text-light outline-hidden py-2 px-4 focus:outline-solid focus:outline-primary"
              // Ariakit's Combobox manually triggers a blur event on virtually
              // blurred items, making them work as if they had actual DOM
              // focus. These blur events might happen after the corresponding
              // focus events in the capture phase, leading Radix Select to
              // close the popover. This happens because Radix Select relies on
              // the order of these captured events to discern if the focus was
              // outside the element. Since we don't have access to the
              // onInteractOutside prop in the Radix SelectContent component to
              // stop this behavior, we can turn off Ariakit's behavior here.
              onBlurCapture={event => {
                event.preventDefault()
                event.stopPropagation()
              }}
            />
          </div>
          <ComboboxList className="flex flex-col gap-2 overflow-y-scroll p-3">
            {loading && (
              <div className="flex flex-row w-full p-5 justify-center items-center">
                <Loading />
              </div>
            )}
            {matches.map(({ label, value }) => (
              <RadixSelect.Item
                key={value}
                value={value}
                asChild
                className="relative group flex cursor-pointer flex-row items-center gap-2 rounded-small p-2 data-[active-item=true]:bg-primary-bright focus:bg-primary-bright focus:outline-hidden pl-8"
              >
                <ComboboxItem>
                  <RadixSelect.ItemIndicator className="absolute left-2 top-1/2 -translate-y-1/2">
                    <HugeiconsIcon
                      icon={Tick01Icon}
                      className="text-text size-4"
                    />
                  </RadixSelect.ItemIndicator>
                  <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
                </ComboboxItem>
              </RadixSelect.Item>
            ))}
          </ComboboxList>
        </RadixSelect.Content>
      </ComboboxProvider>
    </RadixSelect.Root>
  )
}
